import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { T } from "@freightsimple/ui-core/Typography/T";
import { formatFileSize } from "@freightsimple/ui-core/Helpers/formatFileSize";
import { FilePdfIcon } from "@phosphor-icons/react/FilePdf";
import { XIcon } from "@phosphor-icons/react/X";
import { truncateInMiddle } from "@/Helpers/truncateInMiddle";
import type { Dispatch, SetStateAction } from "react";
import type { DropzoneState } from "react-dropzone";

// Drag feedback: green accepts, red rejects, blue is a drag in progress that
// hasn't resolved either way yet.
function dropBorderClass(
  isDragAccept: boolean,
  isDragReject: boolean,
  isDragActive: boolean,
): string {
  if (isDragAccept) return "border-green";
  if (isDragReject) return "border-red";
  if (isDragActive) return "border-blue";
  return "border-border-strong";
}

function displayPdfName(fileName: string): string {
  return fileName.length > 50 ? truncateInMiddle(fileName) : fileName;
}

export interface CustomsDocsDropzoneProps {
  files: Array<File>;
  setFiles: Dispatch<SetStateAction<Array<File>>>;
  dropzoneState: DropzoneState;
}

export function CustomDocsDropzone({
  files,
  setFiles,
  dropzoneState,
}: CustomsDocsDropzoneProps) {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = dropzoneState;

  function handleRemoveFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <Stack gap={3}>
      <div
        {...getRootProps()}
        className={`flex w-full cursor-pointer flex-col items-center gap-1 rounded-xl border-2 border-dashed bg-secondary px-5 py-8 text-center transition-[border] duration-[240ms] ease-in-out outline-none ${dropBorderClass(
          isDragAccept,
          isDragReject,
          isDragActive,
        )}`}
      >
        <input {...getInputProps()} />
        <Icon icon={FilePdfIcon} size="3xl" weight="fill" color="text-blue" />
        <T weight="medium" align="center">
          Drop or click to add PDFs
        </T>
        <T size="sm" color="muted">
          PDF only, up to 10 files
        </T>
      </div>

      {/* The chosen files sit outside the drop target so they read as a list
          of what will be uploaded, not as more instructions. */}
      {files.length > 0 && (
        <ul
          className="m-0 flex list-none flex-col divide-y divide-border rounded-lg border border-border bg-card p-0"
          data-testid="customs-docs-selected-files"
        >
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex min-w-0 items-center gap-2 py-1.5 pr-1.5 pl-3"
            >
              <Icon
                icon={FilePdfIcon}
                size="lg"
                weight="fill"
                color="text-blue"
              />
              <span className="min-w-0 flex-1">
                <T size="sm" truncate>
                  {displayPdfName(file.name)}
                </T>
              </span>
              {/* Lets the customer spot a wrong or empty file before uploading. */}
              <T size="xs" color="muted" nowrap>
                {formatFileSize(file.size)}
              </T>
              <Button
                iconAction
                icon={XIcon}
                tooltip={`Remove ${file.name}`}
                onClick={() => handleRemoveFile(index)}
              />
            </li>
          ))}
        </ul>
      )}
    </Stack>
  );
}
